// src/components/ModalSignIn.js

import React, { useContext, useState, useEffect, useRef } from 'react';
import API from '../api/client';
import { ModalContext } from '../context/ModalContext';
import { AuthContext } from '../context/AuthContext';
import { LikesContext } from '../context/LikesContext';
import { TabContext } from '../context/TabContext';
import { useNavigate } from 'react-router-dom';
import { useGoogleLogin } from "@react-oauth/google";
import googleIcon from '../assets/google-icon.svg';
import ModalFPassword from './ModalFPassword';
import './ModalSignIn.css';

const ModalSignIn = ({ initialTab = 'login' }) => {
  const { closeModal } = useContext(ModalContext);
  const { login } = useContext(AuthContext);
  const { loadFavorites } = useContext(LikesContext);
  const { activeTab, setActiveTab } = useContext(TabContext);

  // 1️⃣ Estado interno de pestañas y animación
  const [activeSubTab, setActiveSubTab] = useState(initialTab);
  const [translateX, setTranslateX] = useState(0);

  // 2️⃣ Al montar: bloquear scroll del body y reset de translateX
  useEffect(() => {
    console.log('[ModalSignIn] mount: bloqueando scroll');
    setTranslateX(0);
    document.body.style.overflow = 'hidden';
    return () => {
      console.log('[ModalSignIn] unmount: desbloqueando scroll');
      document.body.style.overflow = '';
    };
  }, []);

  // 3️⃣ Cierra el modal actual
  const handleClose = () => {
    console.log('[ModalSignIn] handleClose');
    closeModal(activeTab);
  };

  // 4️⃣ Alternar subpestañas login/register
  const switchTab = (tab) => {
    console.log(`[ModalSignIn] switchTab -> ${tab}`);
    setActiveSubTab(tab);
  };

  // 5️⃣ Manejo de éxito: recibimos el "authorization code" de Google
  const handleGoogleSuccess = async (authResponse) => {
    const code = authResponse.code;
    console.log('[ModalSignIn] handleGoogleSuccess, code:', code);

    try {
      // 5.1) Intercambiar el code por nuestros JWT
      const resp = await API.exchangeGoogleCode(code);
      console.log('[ModalSignIn] API.exchangeGoogleCode response:', resp.data);

      const { access, refresh, user } = resp.data;

      // 5.2) Guardar refresh token
      localStorage.setItem('refreshToken', refresh);

      console.log('[ModalSignIn] usuario recibido tras Google-login:', user);

      // 5.3) Actualizar contexto de autenticación
      login(access, user);

      // 5.4) Redirigir según tipo de usuario
      if (user.is_professional) {
        setActiveTab('prof_pets');
      } else {
        await loadFavorites();
      }

      // 5.5) Cerrar modal
      handleClose();
    } catch (err) {
      console.error('[ModalSignIn] Google login error data:', err.response?.data);
      console.error(err);
    }
  };

  // 6️⃣ Configurar el hook de Google para PKCE (flow "auth-code")
  const googleLogin = useGoogleLogin({
    flow: 'auth-code',                 // Usamos Authorization Code (PKCE)
    onSuccess: handleGoogleSuccess,    // Recibimos { code }
    onError: (error) => {
      console.error('[ModalSignIn] Error con Google Login:', error);
    },
  });

  return (
    <div className="modalCard" onClick={handleClose}>
      <div className="modalSingIn-body"
        onClick={(e) => {
          console.log('[ModalSignIn] inner card clicked, propagation stopped');
          e.stopPropagation();
        }}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: translateX === 0 ? 'transform 0.3s ease' : 'none',
        }}
      >
        {/* Botón cerrar */}
        <button className="modalSignIn-close" onClick={handleClose}>
          &times;
        </button>

        {/* Pestañas login / register */}
        <div className="modal-tabs">
          <button
            className={activeSubTab === 'login' ? 'active' : ''}
            onClick={() => switchTab('login')}
          >
            Iniciar sesión
          </button>
          <button
            className={activeSubTab === 'register' ? 'active' : ''}
            onClick={() => switchTab('register')}
          >
            Crear cuenta
          </button>
        </div>

        {/* ——— Botón Google ——— */}
        <div className="google-login-container">
          <button
            className="google-login-btn"
            onClick={() => {
              // **Antes** de lanzar la ventana de Google, metemos un log de las variables
              console.log('[ModalSignIn] ENV:', {
                REACT_APP_GOOGLE_CLIENT_ID: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                REACT_APP_GOOGLE_REDIRECT_URI: process.env.REACT_APP_GOOGLE_REDIRECT_URI,
                REACT_APP_GOOGLE_CLIENT_SECRET: process.env.REACT_APP_GOOGLE_CLIENT_SECRET,
              });
              console.log('[ModalSignIn] googleLogin() triggered');
              googleLogin();
            }}
          >
            <img src={googleIcon} alt="G" className="google-login-icon" />
            Iniciar sesión con Google
          </button>
        </div>

        {/* Contenido según pestaña */}
        <div className="modal-body">
          {activeSubTab === 'login' ? (
            <LoginForm
              onSuccess={handleClose}
              login={login}
              loadFavorites={loadFavorites}
              setActiveTab={setActiveTab}
              activeTab={activeTab}
            />
          ) : (
            <RegisterForm onSuccess={handleClose} />
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalSignIn;

/** Formulario de Login */
function LoginForm({ onSuccess, login, loadFavorites, setActiveTab, activeTab }) {
  const [loginInput, setLoginInput] = useState('');
  const [password, setPassword] = useState('');
  const [showPasswordLogin, setShowPasswordLogin] = useState(false);
  const [error, setError] = useState(null);

  const navigate = useNavigate();

  const { openModal } = useContext(ModalContext);

  // Procesa likes pendientes tras iniciar sesión
  const processPendingLikes = async (token) => {
    const pendingStr = localStorage.getItem('pendingLikes');
    if (!pendingStr) return;
    const pending = JSON.parse(pendingStr);
    for (const petId of pending) {
      try {
        await API.post(
          `/pets/${petId}/like/`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error(`Error procesando like pendiente ${petId}:`, err);
      }
    }
    localStorage.removeItem('pendingLikes');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      // 1️⃣ Hacemos login y recibimos tokens + userInfo
      const response = await API.post('/auth/token/', {
        login: loginInput,
        password,
      });
      const { access, refresh, user: userInfo } = response.data;

      // 2️⃣ Actualizamos AuthContext (guarda token y userInfo)
      login(access, userInfo);

      // 3️⃣ Guardamos el refreshToken para posibles renovaciones
      localStorage.setItem('refreshToken', refresh);

      // 4️⃣ Redirección según tipo de usuario
      if (userInfo.is_professional) {
        setActiveTab('prof_pets');
      } else {
        // 5️⃣ Procesamos likes pendientes
        await processPendingLikes(access);
        // 6️⃣ Cargamos favoritos
        await loadFavorites();
      }

      // 7️⃣ Redirigir al Home tras éxito
      navigate('/');

      // 8️⃣ Cerramos el modal
      onSuccess();
    } catch (err) {
      console.error('Login error:', err);
      setError('Error al iniciar sesión. Verifica tus credenciales.');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      {error && <div className="error">{error}</div>}
      <div className="form-group">
        <label>Login (email o usuario):</label>
        <input
          type="text"
          value={loginInput}
          onChange={(e) => setLoginInput(e.target.value)}
          required
        />
      </div>
      <div className="form-group" style={{ position: 'relative' }}>
        <label>Contraseña:</label>
        <input
          type={showPasswordLogin ? 'text' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          style={{ paddingRight: '2.5rem' }} // para dejar espacio al icono
        />
        <span
          onClick={() => setShowPasswordLogin((prev) => !prev)}
          style={{
            position: 'absolute',
            right: '10px',
            top: '35px', // ajusta según tu CSS
            cursor: 'pointer',
            userSelect: 'none'
          }}
        >
          {showPasswordLogin ? '🙈' : '👁️'}
        </span>
      </div>
      <button type="submit" className="btn">Iniciar sesión</button>
      {/* ← Enlace "He olvidado mi contraseña" justo debajo */}
      <div className="forgot-password-link">
        <button
          type="button"
          className="link-forgot"
          onClick={() => {
            openModal(
            <ModalFPassword />,
              {
                tab: activeTab,
                overlayClassName: 'forgot-modal-overlay',
              }
            );
          }}
        >
          He olvidado mi contraseña
        </button>
      </div>
    </form>
  );
}

/** Formulario de Registro en 3 pasos */
function RegisterForm({ onSuccess }) {
  // ——— Estados Step 1 ———
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [phone, setPhone] = useState(''); // 9 dígitos sin el +34

  // ——— Estados de validación username ———
  const [usernameValid, setUsernameValid] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [usernameMessage, setUsernameMessage] = useState('');
  const debounceTimer = useRef(null);

  // ——— Estados de mostrar/ocultar contraseña ———
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ——— Estados de requisitos de contraseña ———
  const [lengthValid, setLengthValid] = useState(false);
  const [upperValid, setUpperValid] = useState(false);
  const [lowerValid, setLowerValid] = useState(false);
  const [numberValid, setNumberValid] = useState(false);

  // ——— Estados generales del formulario ———
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);            // Paso 1 o Paso 2
  const [chosenMethod, setChosenMethod] = useState(''); // 'email' o 'sms'
  const [loading, setLoading] = useState(false);

  // ——— Estados para SMS ———
  const [smsRequested, setSmsRequested] = useState(false); // Ya pedimos un código
  const [smsCode, setSmsCode] = useState('');              // El usuario introduce el código
  const [smsSent, setSmsSent] = useState(false);           // Indica que ya llegó la respuesta 200 del envío
  const [secondsLeft, setSecondsLeft] = useState(300);     // 5 minutos = 300 segundos
  const [attemptsLeft, setAttemptsLeft] = useState(3);     // 3 reintentos en total
  const timerRef = useRef(null);

  // ** Nuevo estado para indicar si el registro/activación se completó con éxito **
  // Si `completed === true`, NO borramos al usuario pendiente cuando se cierre el modal.
  const [completed, setCompleted] = useState(false);

  // Contextos para hacer login y cargar favoritos
  const { login } = useContext(AuthContext);
  const { loadFavorites } = useContext(LikesContext);
  const { setActiveTab } = useContext(TabContext);

  // Función que inicia el temporizador de 5 minutos
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSecondsLeft(300);
    timerRef.current = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ——— Al desmontar RegisterForm: si estamos en flujo SMS pendiente, borramos usuario ────
  useEffect(() => {
    return () => {
      // Limpiar temporizador si existe
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }

      // Si el flujo SMS fue realmente solicitado al backend (smsRequested===true) y no completamos, borramos:
      if (chosenMethod === 'sms' && smsRequested && !completed) {
        API.post('/auth/delete_pending_user/', { username })
          .then(() => {
            console.log('[RegisterForm] Usuario pendiente borrado tras cerrar modal.');
          })
          .catch((err) => {
            console.error(
              '[RegisterForm] Error borrando usuario pendiente:',
              err.response?.data || err
            );
          });
      }
    };
  }, [chosenMethod, smsRequested, completed, username]);

  // ——— Validación de contraseña en tiempo real ———
  useEffect(() => {
    const pwd = password;
    setLengthValid(pwd.length >= 6);
    setUpperValid(/[A-Z]/.test(pwd));
    setLowerValid(/[a-z]/.test(pwd));
    setNumberValid(/[0-9]/.test(pwd));
  }, [password]);

  // ——— Validación de disponibilidad de username ———
  useEffect(() => {
    // 1) Definimos la RegExp dentro del efecto para que no cambie cada render
    const usernameRegex = /^[a-zA-Z0-9_]{6,}$/;

    // Reiniciamos estados
    setUsernameAvailable(null);
    setUsernameMessage('');
    setUsernameValid(false);

    // Cancelamos timeout anterior
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (username.trim() === '') {
      // Si está vacío, no validamos nada
      return;
    }

    debounceTimer.current = setTimeout(async () => {
      // 2) Validación local con RegExp
      if (!usernameRegex.test(username)) {
        setUsernameValid(false);
        setUsernameMessage('El usuario debe tener al menos 6 caracteres alfanuméricos.');
        setUsernameAvailable(false);
        return;
      }

      // 3) Si pasa la RegExp, marcamos localmente y llamamos al backend
      setUsernameValid(true);
      try {
        console.log(
          '[RegisterForm] Llamando a:',
          `${API.defaults.baseURL}/auth/check_username/?username=${username}`
        );
        const resp = await API.get(`/auth/check_username/?username=${username}`);
        const { available } = resp.data;
        setUsernameAvailable(available);
        setUsernameMessage(available ? 'Usuario disponible' : 'Usuario no disponible');
      } catch (err) {
        console.error('[RegisterForm] Error comprobando username:', err);
        setUsernameAvailable(false);
        setUsernameMessage('No se pudo comprobar disponibilidad.');
      }
    }, 500);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [username]);

  // ——— Manejar cambio de teléfono (solo dígitos y máximo 9) ———
  const handlePhoneChange = (e) => {
    // Filtramos para que solo queden dígitos
    const digitsOnly = e.target.value.replace(/\D/g, '');
    // Limitar a 9 dígitos (longitud típica de número móvil español sin prefijo)
    setPhone(digitsOnly.slice(0, 9));
  };

  // —— Paso 1: validaciones y avanzar a Step 2 ——
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validaciones básicas del resto de campos
    if (!username.trim() || !email.trim() || !password || !password2 ) {  //|| !phone.trim()
      setError('Todos los campos marcados con * son obligatorios.');
      return;
    }
    if (password !== password2) {
      setError('Las contraseñas deben coincidir.');
      return;
    }
    if (!lengthValid || !upperValid || !lowerValid || !numberValid) {
      setError('La contraseña no cumple todos los requisitos.');
      return;
    }
    if (!usernameValid) {
      setError('El usuario debe tener al menos 6 caracteres alfanuméricos.');
      return;
    }
    if (usernameAvailable === false) {
      setError('El nombre de usuario no está disponible.');
      return;
    }
    // Si phone no está vacío, debe tener 9 dígitos
    if (phone.trim() !== '' && phone.length !== 9) {
      setError('El número de móvil debe tener 9 dígitos.');
      return;
    }

    // —— NUEVO: comprobar que el email no existe todavía ——
    try {
      const resp = await API.get(`/auth/check_email/?email=${encodeURIComponent(email.trim())}`);
      // esperamos { available: true } o { available: false }
      if (!resp.data.available) {
        setError('Ese correo ya está registrado. Usa otro o inicia sesión.');
        return;
      }
    } catch (err) {
      console.error('[RegisterForm] Error comprobando email:', err);
      setError('No se pudo verificar el email. Inténtalo de nuevo.');
      return;
    }


    console.log('[RegisterForm] Step 1 completado, avanzando a Step 2');
    setStep(2);
  };

  // —— Paso 2: elegir métódo (email o sms) ——
  //   - Ahora “Enviar por SMS” sólo SETEA chosenMethod, y mostramos el input de teléfono + botón “Confirmar”
  const handleMethodSelection = async (method) => {
    setError(null);
    setChosenMethod(method);

    // Si es email, directamente disparamos el registro
    if (method === 'email') {
      setLoading(true);
      try {
        const fullPhone = phone.trim() === '' ? '' : `+34${phone}`;
        const payload = {
          username,
          email,
          password,
          password2,
          phone: fullPhone,
          verification_method: 'email',
        };
        await API.post('/auth/register/', payload);
        console.log('[RegisterForm] Registro enviado por correo correctamente');
        setCompleted(true);
        setStep(3);
      } catch (err) {
        console.error('[RegisterForm] Error al registrar por email:', err.response?.data || err);
        setError('Error al registrar por correo. Verifica la información e intenta nuevamente.');
      } finally {
        setLoading(false);
      }
    }
    // Si es sms, **no enviamos todavía**: mostramos el campo de teléfono y el botón “Confirmar”
    if (method === 'sms') {
      // sólo marcamos que ya elegimos SMS, no disparamos API todavía

    }
  };

  // —— Confirmar teléfono y enviar realmente el SMS ——
  const handleConfirmAndSendSMS = async () => {
    setError(null);
    setLoading(true);
    try {
      const fullPhone = `+34${phone}`;
      const payload = { username, email, password, password2, phone: fullPhone };
      const resp = await API.post('/auth/request_sms_code/', payload);
      console.log('[RegisterForm] SMS de código solicitado correctamente');

      // Actualizamos cuántos intentos le quedan
      setAttemptsLeft(resp.data.attempts_left);

      // Ahora sí marcamos que el backend recibió la petición y creó el usuario (pendiente)
      setSmsRequested(true);

      // Indicamos que ya salió el SMS y arrancamos temporizador
      setSmsSent(true);
      startTimer();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error genérico';
      // Si backend dice “El usuario fue eliminado…”, volvemos al paso 1
      if (detail.includes('El usuario ha sido eliminado')) {
        setError(detail);
        setStep(1);
        // Resetear estados Step 1
        setUsername('');
        setEmail('');
        setPassword('');
        setPassword2('');
        setPhone('');
        setSmsRequested(false);
        setSmsSent(false);
        setSecondsLeft(0);
        setAttemptsLeft(3);
        return;
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  // —— Verificar el código SMS de 4 dígitos ——
  const handleVerifyCode = async () => {
    setError(null);
    if (smsCode.trim().length !== 4) {
      setError('El código debe tener 4 dígitos.');
      return;
    }
    setLoading(true);
    try {
      // 2) Llamar a /auth/verify_sms_code/ con { username, code }
      const resp = await API.post('/auth/verify_sms_code/', {
        username,
        code: smsCode.trim(),
      });
      console.log('[RegisterForm] Código SMS verificado correctamente:', resp.data);

      // 3) Si está OK, recibimos { access, refresh, user }
      const { access, refresh, user } = resp.data;

      // 4) Guardar refresh token en localStorage
      localStorage.setItem('refreshToken', refresh);

      // 5) Actualizar AuthContext
      login(access, user);

      // Marcamos completed = true para que el cleanup no borre nada
      setCompleted(true);

      if (user.is_professional) {
        setActiveTab('prof_pets');
      } else {
        await loadFavorites();
      }
      onSuccess();
    } catch (err) {
      console.error('[RegisterForm] Error verificando código SMS:', err.response?.data || err);
      setError(err.response?.data?.detail || 'Código incorrecto o expirado.');
    } finally {
      setLoading(false);
    }
  };

  // ——— Temporizador de 5 minutos (gestiona smsSent) ———
  useEffect(() => {
    if (smsSent) {
      // Arrancar cuenta atrás de 300s
      setSecondsLeft(300);
      timerRef.current = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    // Cleanup del intervalo si smsSent cambia o componente se desmonta
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [smsSent]);

  // —— Reenviar código cuando timer llegue a 0 ——
  const handleResendCode = async () => {
    setError(null);
    if (attemptsLeft <= 0) {
      setError('Has alcanzado el límite de 3 reenvíos de SMS.');
      return;
    }
    setLoading(true);
    try {
      // Llamamos igual que “Enviar por SMS”: /auth/request_sms_code/
      const fullPhone = `+34${phone}`;
      const payload = { username, email, password, password2, phone: fullPhone };
      const resp = await API.post('/auth/request_sms_code/', payload);
      console.log('[RegisterForm] Reenvío SMS solicitado correctamente');

      setSmsCode('');
      setSmsSent(true);
      startTimer();
      setAttemptsLeft(resp.data.attempts_left);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error genérico';
      // Si el backend devolvió que "El usuario ha sido eliminado…", resetear al usuario pendiente
      if (detail.includes('El usuario ha sido eliminado')) {
        setError(detail);
        setStep(1);
        // Resetear Step 1
        setUsername('');
        setEmail('');
        setPassword('');
        setPassword2('');
        setPhone('');
        setSmsRequested(false);
        setSmsSent(false);
        setSecondsLeft(0);
        setAttemptsLeft(3);
        return;
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  /** Renderizado por pasos **/
  return (
    <>
      {/* ——— Paso 1 ——— */}
      {step === 1 && (
        <form onSubmit={handleStep1Submit} className="form">
          <h3>Crear cuenta – Paso 1 de 2</h3>
          {error && <div className="error">{error}</div>}

          {/* Campo Usuario con validación */}
          <div className="form-group">
            <label>
              Usuario <span className="required">*</span>:
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            {usernameMessage && (
              <small className={usernameAvailable ? 'valid' : 'invalid'}>
                {usernameMessage}
              </small>
            )}
          </div>

          {/* Campo Email */}
          <div className="form-group">
            <label>
              Email <span className="required">*</span>:
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Campo Contraseña con toggle ojo */}
          <div className="form-group relative">
            <label>
              Contraseña <span className="required">*</span>:
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="toggle-btn"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
            {password.length > 0 && (
              <ul className="password-requirements">
                <li className={lengthValid ? 'valid' : 'invalid'}>Mínimo 6 caracteres</li>
                <li className={upperValid ? 'valid' : 'invalid'}>Al menos una letra mayúscula</li>
                <li className={lowerValid ? 'valid' : 'invalid'}>Al menos una letra minúscula</li>
                <li className={numberValid ? 'valid' : 'invalid'}>Al menos un número</li>
              </ul>
            )}
          </div>

          {/* Campo Confirmar contraseña con toggle */}
          <div className="form-group relative">
            <label>
              Confirmar contraseña <span className="required">*</span>:
            </label>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={password2}
              onChange={(e) => setPassword2(e.target.value)}
              required
            />
            <button
              type="button"
              className="toggle-btn"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              tabIndex={-1}
            >
              {showConfirmPassword ? '🙈' : '👁️'}
            </button>
          </div>

          {/* Campo Teléfono (Paso 1) */}
          <div className="form-group">
            <label className="phone-label">Teléfono:</label>
            <div className="phone-group">
              <span className="phone-prefix">+34</span>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="6XX XXX..."
                className="phone-input"
              />
            </div>
          </div>

          <button type="submit" className="btn">Siguiente</button>
        </form>
      )}

      {/* ——— Paso 2 ——— */}
      {step === 2 && (
        <div className="form">
          <h3>Crear cuenta – Paso 2 de 2</h3>
          {error && <div className="error">{error}</div>}

          <p>Elige cómo quieres recibir el enlace de activación:</p>

          {/* Botón Enviar por correo */}
          <div className="form-group">
            <button
              type="button"
              className="btn"
              onClick={() => handleMethodSelection('email')}
              disabled={loading}
            >
              Enviar por correo electrónico
            </button>
          </div>

          {/* Botón Enviar por SMS: sólo setea chosenMethod y muestra input para confirmar */}
          <div className="form-group">
            <button
              type="button"
              className="btn"
              onClick={() => handleMethodSelection('sms')}
              disabled={loading || smsRequested}
            >
              Enviar por SMS
            </button>
          </div>

          {/* Si el usuario eligió SMS: mostrar campo Teléfono + botón “Confirmar” */}
          {chosenMethod === 'sms' && (
            <div className="sms-confirm-section">
              <p>Por favor, confirma o corrige tu número de teléfono:</p>

              {/* Campo Teléfono (prellenado) */}
              <div className="form-group">
                <label className="phone-label">
                  Teléfono <span className="required">*</span>:
                </label>
                <div className="phone-group">
                  <span className="phone-prefix">+34</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={handlePhoneChange}
                    placeholder="6XX XXX..."
                    className="phone-input"
                  />
                </div>
              </div>

              {/* Botón Verificar Número y Enviar SMS */}
              <div className="form-group">
                <button
                  type="button"
                  className="btn"
                  onClick={handleConfirmAndSendSMS}
                  disabled={loading || phone.length !== 9}
                >
                  Confirmar número y enviar SMS
                </button>
              </div>
            </div>
          )}

          {/* Una vez enviado el SMS, mostramos el input de 4 dígitos y el temporizador */}
          {chosenMethod === 'sms' && smsSent && (
            <>
              <p>
                Hemos enviado un código SMS de 4 dígitos a tu número. Introduce aquí tu código:
              </p>

              <div className="form-group">
                <input
                  type="text"
                  value={smsCode}
                  onChange={(e) =>
                    setSmsCode(e.target.value.replace(/\D/g, '').slice(0, 4))
                  }
                  placeholder="Código (4 dígitos)"
                  className="phone-4digit"
                />
              </div>

              <div className="form-group">
                <button
                  type="button"
                  className="btn"
                  onClick={handleVerifyCode}
                  disabled={smsCode.length !== 4 || loading}
                >
                  Verificar código
                </button>
              </div>

              {/* Temporizador (5 minutos) */}
              {secondsLeft > 0 && (
                <p>
                  Si no recibes el código, podrás solicitar uno nuevo en{' '}
                  <strong>
                    {String(Math.floor(secondsLeft / 60)).padStart(2, '0')}:
                    {String(secondsLeft % 60).padStart(2, '0')}
                  </strong>
                </p>
              )}

              {/* Botón Reenviar cuando termine el temporizador */}
              {secondsLeft === 0 && attemptsLeft > 0 && (
                <div className="form-group">
                  <button
                    type="button"
                    className="btn"
                    onClick={handleResendCode}
                    disabled={loading}
                  >
                    Reenviar código SMS ({attemptsLeft}{' '}
                    {attemptsLeft > 1 ? 'intentos' : 'intento'} restante
                    {attemptsLeft > 1 ? 's' : ''})
                  </button>
                </div>
              )}

              {/* Mensaje cuando ya no quedan reintentos */}
              {secondsLeft === 0 && attemptsLeft === 0 && (
                <p className="error">
                  Has alcanzado el límite de 3 reenvíos de SMS.
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* ——— Paso 3 (solo modo email) ——— */}
      {step === 3 && chosenMethod === 'email' && (
        <div className="form">
          <h3>Registro completado</h3>
          <p>
            Se ha enviado un correo de activación. Por favor, revisa tu bandeja
            (y spam).
          </p>
          <button type="button" className="btn" onClick={onSuccess}>
            Cerrar
          </button>
        </div>
      )}
    </>
  );
}

export { RegisterForm };
